import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchEmployees, fetchEmployeeStats, syncEmployeesFromEntra } from '../api/employees';
import type { EmployeeListItem, EmployeeStats, EntraSyncResult } from '../api/employees';
import StatusBadge from '../components/StatusBadge';
import type { CvStatus } from '../types';
import SkillBadge from '../components/SkillBadge';

type SortField = 'full_name' | 'email' | 'department' | 'job_title' | 'status' | 'cv_count' | 'last_cv_date' | 'skills';
type SortOrder = 'asc' | 'desc';

export default function Employees() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<EntraSyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('full_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [showInactive, setShowInactive] = useState(false);
  const [stats, setStats] = useState<EmployeeStats | null>(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search input so we don't fire a request on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, statsResult] = await Promise.all([
        fetchEmployees({
          page,
          page_size: 50,
          sort_by: sortField,
          order: sortOrder,
          search: debouncedSearch || undefined,
          status: showInactive ? undefined : 'ACTIVE',
        }),
        fetchEmployeeStats(),
      ]);
      setEmployees(result.items);
      setTotalPages(result.total_pages);
      setStats(statsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('employees.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, showInactive, sortField, sortOrder, t]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  async function handleSyncEntra() {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const result = await syncEmployeesFromEntra();
      setSyncResult(result);
      await loadEmployees();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : t('employees.syncFailed', { error: '' }));
    } finally {
      setSyncing(false);
    }
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  }

  function sortIndicator(field: SortField) {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  const pctWithCv = stats && stats.total > 0 ? Math.round((stats.with_cv / stats.total) * 100) : 0;
  const pctWithRecentCv = stats && stats.total > 0 ? Math.round((stats.with_recent_cv / stats.total) * 100) : 0;

  const thClass = 'cursor-pointer select-none hover:text-brand-700';

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">{t('employees.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('employees.subtitle')}
        </p>
      </header>

      {/* Entra ID Sync */}
      <section className="card flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{t('employees.entraSync')}</h2>
          <p className="text-xs text-gray-500">{t('employees.entraSyncHint')}</p>
        </div>
        <button
          onClick={handleSyncEntra}
          disabled={syncing}
          className="btn-primary text-sm"
          aria-busy={syncing}
        >
          {syncing ? t('employees.syncing') : t('employees.syncFromEntra')}
        </button>
      </section>

      {syncResult && (
        <div role="status" aria-live="polite" className="rounded bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {t('employees.syncComplete', {
            fetched: syncResult.fetched,
            created: syncResult.created,
            updated: syncResult.updated,
            deactivated: syncResult.deactivated,
            skipped: syncResult.skipped,
          })}
        </div>
      )}

      {syncError && (
        <div role="alert" className="rounded bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {t('employees.syncFailed', { error: syncError })}
        </div>
      )}

      {/* Stats */}
      {stats && stats.total > 0 && (
        <section aria-label={t('employees.stats')} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500">{t('employees.activeEmployees')}</p>
          </div>
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-gray-900">{stats.with_cv}</p>
            <p className="text-xs text-gray-500">{t('employees.withCv', { pct: pctWithCv })}</p>
          </div>
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-gray-900">{stats.with_recent_cv}</p>
            <p className="text-xs text-gray-500">{t('employees.recentCv', { pct: pctWithRecentCv })}</p>
          </div>
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-gray-900">{stats.total - stats.with_cv}</p>
            <p className="text-xs text-gray-500">{t('employees.missingCv', { pct: 100 - pctWithCv })}</p>
          </div>
        </section>
      )}

      {/* Search & filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-48 max-w-md">
          <label htmlFor="employee-search" className="sr-only">
            {t('employees.searchLabel')}
          </label>
          <input
            id="employee-search"
            type="search"
            placeholder={t('employees.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input w-full"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          {t('employees.showInactive')}
        </label>
      </div>

      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
          <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">&#x27F3;</span>
          {t('employees.loading')}
        </div>
      )}

      {!loading && !error && (
        <>
          {employees.length === 0 ? (
            <div className="card text-center py-12 text-gray-500 text-sm">
              {t('employees.noEmployees')}
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="data-table" aria-label={t('employees.employeeList')}>
                  <thead>
                    <tr>
                      <th scope="col" className={thClass} onClick={() => handleSort('full_name')}>
                        {t('employees.name')}{sortIndicator('full_name')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('email')}>
                        {t('employees.email')}{sortIndicator('email')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('department')}>
                        {t('employees.department')}{sortIndicator('department')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('job_title')}>
                        {t('employees.jobTitle')}{sortIndicator('job_title')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('status')}>
                        {t('employees.status')}{sortIndicator('status')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('cv_count')}>
                        {t('employees.cvs')}{sortIndicator('cv_count')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('last_cv_date')}>
                        {t('employees.lastCv')}{sortIndicator('last_cv_date')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('skills')}>
                        {t('employees.skills')}{sortIndicator('skills')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => (
                      <>
                        <tr key={emp.id}>
                          <td>
                            <div className="flex items-center gap-2">
                              {emp.photo ? (
                                <img
                                  src={emp.photo}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover shrink-0"
                                />
                              ) : (
                                <span className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-500 shrink-0" aria-hidden="true">
                                  {emp.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                                </span>
                              )}
                              <span className="font-medium text-gray-900">{emp.full_name}</span>
                            </div>
                          </td>
                          <td className="text-gray-600 text-xs">{emp.email}</td>
                          <td className="text-gray-600 text-xs">{emp.department ?? '-'}</td>
                          <td className="text-gray-600 text-xs">{emp.job_title ?? '-'}</td>
                          <td>
                            <StatusBadge status={(emp.status?.toLowerCase() ?? 'active') as CvStatus} />
                          </td>
                          <td className="text-xs text-gray-500">{emp.cv_count}</td>
                          <td className="text-xs text-gray-500">
                            {emp.last_cv_date ? (
                              <time dateTime={emp.last_cv_date}>
                                {new Date(emp.last_cv_date).toLocaleDateString(dateLocale, {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                })}
                              </time>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td>
                            {emp.skills && emp.skills.length > 0 ? (
                              <button
                                onClick={() => setExpandedId(expandedId === emp.id ? null : emp.id)}
                                className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500"
                                aria-expanded={expandedId === emp.id}
                                aria-controls={`skills-${emp.id}`}
                              >
                                {t('employees.skillsCount', { count: emp.skills.length })}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-300">0</span>
                            )}
                          </td>
                        </tr>
                        {expandedId === emp.id && emp.skills && emp.skills.length > 0 && (
                          <tr key={`${emp.id}-skills`}>
                            <td colSpan={8} className="bg-gray-50 px-4 py-3">
                              <ul
                                id={`skills-${emp.id}`}
                                className="flex flex-wrap gap-1.5"
                              >
                                {emp.skills.map((skill) => (
                                  <SkillBadge key={skill} skill={skill} matched />
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label={t('employees.pagination')} className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs disabled:opacity-40"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600">
                {t('common.pageOf', { page, totalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-secondary text-xs disabled:opacity-40"
              >
                {t('common.next')}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
